import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
});

// Attach token
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-refresh on 401
API.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refresh = localStorage.getItem('refresh_token');
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/auth/token/refresh/`,
          { refresh }
        );
        localStorage.setItem('access_token', data.access);
        original.headers.Authorization = `Bearer ${data.access}`;
        return API(original);
      } catch {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// Auth
export const authAPI = {
  login: (data) => API.post('/auth/login/', data),
  logout: (refresh) => API.post('/auth/logout/', { refresh }),
  changePassword: (data) => API.post('/auth/change-password/', data),
  me: () => API.get('/auth/me/'),
};

// Users
export const usersAPI = {
  list: (params) => API.get('/users/', { params }),
  create: (data) => API.post('/users/', data),
  update: (id, data) => API.patch(`/users/${id}/`, data),
  delete: (id) => API.delete(`/users/${id}/`),
};

// Increment Cycles
export const cyclesAPI = {
  list: (params) => API.get('/increment-cycles/', { params }),
  get: (id) => API.get(`/increment-cycles/${id}/`),
  create: (data) => API.post('/increment-cycles/', data),
  update: (id, data) => API.patch(`/increment-cycles/${id}/`, data),
  complete: (id) => API.post(`/increment-cycles/${id}/complete/`),
  disable: (id) => API.post(`/increment-cycles/${id}/disable/`),
  syncZoho: (id) => API.post(`/increment-cycles/${id}/sync-zoho/`),
  uploadSalaryHistory: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post(`/increment-cycles/${id}/upload-salary-history/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadPMS: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post(`/increment-cycles/${id}/upload-pms-data/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  generateRecommendations: (id) => API.post(`/increment-cycles/${id}/generate-recommendations/`),
  downloadConsolidated: (id) => API.get(`/increment-cycles/${id}/download/`, { responseType: 'blob' }),
  downloadDeptwise: (id) => API.get(`/increment-cycles/${id}/download-deptwise/`, { responseType: 'blob' }),
  downloadSalaryTemplate: () => API.get('/increment-cycles/templates/salary-history/', { responseType: 'blob' }),
  downloadPMSTemplate: () => API.get('/increment-cycles/templates/pms-data/', { responseType: 'blob' }),
  getHodData: (id, params) => API.get(`/increment-cycles/${id}/hod-data/`, { params }),
  updateHodData: (id, recId, data) => API.patch(`/increment-cycles/${id}/hod-data/${recId}/`, data),
  hodSubmit:  (id, viewAsId) => API.post(`/increment-cycles/${id}/hod-submit/`,  viewAsId ? { view_as: viewAsId } : {}),
  hrSubmit:   (id, viewAsId) => API.post(`/increment-cycles/${id}/hr-submit/`,   viewAsId ? { view_as: viewAsId } : {}),
  mgmtSubmit: (id)           => API.post(`/increment-cycles/${id}/mgmt-submit/`, {}),
  getDepartments: () => API.get('/increment-cycles/departments/'),
};

export default API;
